import React, { useState, useRef, useEffect } from "react";
import { Pencil, Check, X, CalendarOff } from "lucide-react";
import CueStatus from "./CueStatus";

const pad = (n) => String(n).padStart(2, "0");

function toLocalInput(iso) {
  const d = iso ? new Date(iso) : (() => {
    const t = new Date();
    t.setHours(17, 0, 0, 0);
    return t;
  })();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// onApply(isoOrNull): parent persists (entries) or updates a local draft.
export default function EditableCue({ entry, onApply, testid }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(toLocalInput(entry.suggested_datetime));
  const ref = useRef(null);

  useEffect(() => {
    if (open) setValue(toLocalInput(entry.suggested_datetime));
  }, [open, entry.suggested_datetime]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const save = () => {
    if (!value) return;
    const iso = new Date(value).toISOString();
    onApply(iso);
    setOpen(false);
  };

  const clear = () => {
    onApply(null);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        data-testid={testid ? `${testid}-trigger` : undefined}
        onClick={() => setOpen((o) => !o)}
        className="group inline-flex items-center gap-1.5 rounded-full transition-all duration-200 hover:opacity-90"
        title="Edit when this comes back"
      >
        <CueStatus entry={entry} testid={testid} />
        <span className="h-6 w-6 rounded-full bg-[var(--surface)] border border-[var(--line)] flex items-center justify-center text-[var(--ink3)] group-hover:text-[var(--pine)] group-hover:border-[var(--pine)]">
          <Pencil size={12} strokeWidth={1.9} />
        </span>
      </button>

      {open && (
        <div
          data-testid={testid ? `${testid}-editor` : undefined}
          className="absolute z-30 mt-2 left-0 w-[290px] rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_12px_32px_rgba(0,0,0,0.12)] p-4"
        >
          <p className="text-xs uppercase tracking-[0.18em] font-bold text-[var(--ink4)] mb-2">
            When should this come back?
          </p>
          <input
            type="datetime-local"
            data-testid={testid ? `${testid}-input` : undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full rounded-xl border border-[var(--line)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--ink)] focus:outline-none focus:border-[var(--pine)]"
          />
          <div className="flex items-center justify-between gap-2 mt-3">
            <button
              type="button"
              data-testid={testid ? `${testid}-clear` : undefined}
              onClick={clear}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--ink3)] hover:text-[var(--pine)] transition-colors"
            >
              <CalendarOff size={13} strokeWidth={1.9} /> Clear cue
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-full flex items-center justify-center text-[var(--ink3)] hover:bg-[var(--surface2)]"
              >
                <X size={15} strokeWidth={2} />
              </button>
              <button
                type="button"
                data-testid={testid ? `${testid}-save` : undefined}
                onClick={save}
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] text-xs font-semibold px-4 py-2 transition-colors"
              >
                <Check size={14} strokeWidth={2.2} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
